import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AdornedInput, FormField, FormHelp, IconSearch } from '@zillow/constellation';
import type { AutocompleteAreaApiType } from 'app/shared/types/ApiResponseTypes';
import AutocompleteArea from 'app/shared/models/AutocompleteArea';
import api from 'app/shared/utils/api';
import debounce from 'lodash/debounce';
import { augmentError, formatForLog } from 'app/shared/utils/errors';

import * as S from './styles';
import type { State } from 'app/shared/types/State';

export type AutocompleteAreaInputType = 'primary' | 'secondary';

interface AutocompleteAreaInputProps {
    submit: (area: AutocompleteArea) => void;
    blockedAreas?: Array<string>;
    type?: AutocompleteAreaInputType;
    error?: string | null;
    placeholder?: string;
}

const logger = log.child('components/AutocompleteAreaInput');

const AutocompleteAreaInput: React.FunctionComponent<AutocompleteAreaInputProps> = (props) => {
    const { submit, type = 'primary', error, blockedAreas = [], placeholder = 'Search by city or ZIP' } = props;
    const dispatch = useDispatch();
    const isMobile = useSelector((state: State) => state.app.device.isMobile);
    const [selectedIdx, setSelectedIdx] = useState<number>(-1);
    const [options, setOptions] = useState<Array<AutocompleteArea>>([]);
    const updateSuggestions = useCallback(
        debounce(async (searchTerm: string) => {
            try {
                // @ts-ignore
                const { data, errors } = await dispatch(api.area.autocomplete(searchTerm));
                if (errors.length > 0) {
                    throw new Error(errors[0].code);
                }
                const areas: Array<AutocompleteArea> = data
                    .map((obj: AutocompleteAreaApiType) => new AutocompleteArea(obj))
                    .filter((area: AutocompleteArea) => !blockedAreas.includes(area.resourceId as string));
                setSelectedIdx(areas.length > 0 ? 0 : -1);
                setOptions(areas);
            } catch (error) {
                augmentError(error, {
                    root: 'components/AutocompleteAreaInput#updateSuggestions'
                });

                logger.error(formatForLog(error), 'AutocompleteAreaInput: Error updating suggestions');
            }
        }, 200),
        [dispatch, setSelectedIdx, setOptions]
    );
    const onSubmit = useCallback(
        (e: React.FormEvent | React.MouseEvent) => {
            e.preventDefault();
            if (isOptionSelected) {
                submit(options[selectedIdx]);
            }
            e.stopPropagation(); // make sure listbox doesnt reappear
        },
        [submit, options, selectedIdx]
    );

    const isOptionSelected = selectedIdx !== -1;

    return (
        <S.Form onSubmit={onSubmit}>
            <FormField error={!!error} description={!!error && <FormHelp>{error}</FormHelp>}>
                <S.Combobox
                    appearance="input"
                    autoCompleteBehavior="none"
                    // @ts-ignore
                    allowSubmitWhileOpen={true}
                    focusFirstOption={true}
                    // @ts-ignore
                    placeholder={placeholder}
                    options={
                        options.map((area) => area.longName).filter((suggestion) => suggestion !== null) as Array<
                            string
                        >
                    }
                    selectCallbackFrequency="always"
                    onOptionSelect={(e) => {
                        const idx = options.findIndex((m) => m.longName === e.value);
                        if (idx !== -1) {
                            submit(options[idx]);
                        }
                    }}
                    renderInput={({ onChange, size, expanded, id, ...rest }) => {
                        return (
                            <AdornedInput
                                input={
                                    <S.SearchInput
                                        {...rest}
                                        id={id}
                                        aria-labelledby="search-adornment"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            if (e.target.value.length >= 3) {
                                                if (
                                                    !isOptionSelected ||
                                                    (isOptionSelected &&
                                                        e.target.value !== options[selectedIdx].longName)
                                                ) {
                                                    updateSuggestions(e.target.value);
                                                }
                                            }
                                            // @ts-ignore
                                            onChange(e);
                                        }}
                                        type={type}
                                        // @ts-ignore
                                        fontType={isMobile ? 'bodySmall' : 'body'}
                                        // TODO: ComboBox should forward these aria roles. Remove thes once that is fixed.
                                        aria-expanded={expanded}
                                        aria-haspopup="listbox"
                                    />
                                }
                                rightAdornment={
                                    <S.SearchAdornment
                                        type={type}
                                        // @ts-ignore
                                        id="search-adornment"
                                        htmlFor={id}
                                        // @ts-ignore
                                        onClick={onSubmit}
                                    >
                                        <IconSearch />
                                    </S.SearchAdornment>
                                }
                            />
                        );
                    }}
                />
            </FormField>
        </S.Form>
    );
};

export default AutocompleteAreaInput;
